import { translateLabel } from "@/i18n/messages";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  StarIcon,
  MapPinIcon,
  HeartIcon,
  CableCarIcon,
  PathIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  CaretDownIcon,
} from "@phosphor-icons/react";
import { DifficultyBar, DifficultyLegend } from "@/components/DifficultyBar";
import { STYLE_LABELS, ACCESS_LABELS } from "@/lib/apiClient";
import { discoveryBucketLabel } from "@/lib/discoveryPresentation";
import { currentDestinationStatusLabel, isTrailCountOnlyRecommendation } from "@/lib/destinationDetail";
import { useAuth } from "@/context/AuthContext";
import { useUnitPreference } from "@/context/UnitPreferenceContext";
import { formatDistanceKm } from "@/lib/units";
import { destinationCoverImage } from "@/lib/destinationCover";
import { toast } from "sonner";
import { parkPath } from "@/lib/routes";
import { buildDiscoveryResultEvidence } from "@/lib/discoveryResultReasons";

const TYPE_STYLES = {
  "Bike Park": "bg-[#CCFF00] text-black",
  "Trail Center": "bg-blue-500 text-white",
  "Trail Area": "bg-emerald-500 text-black",
};

const BUCKET_STYLES = {
  closest: "border-amber-400/30 bg-amber-400/10 text-amber-200",
  nearby: "border-sky-400/30 bg-sky-400/10 text-sky-200",
};

const STATUS_STYLES = {
  OPEN_NOW: "text-emerald-300",
  CLOSED_NOW: "text-red-300",
  RESTRICTED: "text-amber-300",
  UNKNOWN: "text-zinc-500",
};

const STATUS_DOT_STYLES = {
  OPEN_NOW: "bg-emerald-400",
  CLOSED_NOW: "bg-red-400",
  RESTRICTED: "bg-amber-300",
  UNKNOWN: "bg-zinc-600",
};

const formatDriveMinutes = (minutes) => {
  if (minutes == null) return null;
  const rounded = Math.max(0, Math.round(minutes));
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

export function ParkCard({
  park,
  active,
  onHover,
  onSelect,
  index = 0,
  showFavorite = true,
  onFavoriteChanged,
  desktopCompact = false,
  compareSelected = false,
  onToggleCompare,
  discoveryCriteria,
  discoveryPreferences,
}) {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useAuth();
  const { unitSystem } = useUnitPreference();
  const fav = isFavorite(park.id, "park");
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const bucketLabel = discoveryBucketLabel(park.discovery_bucket);
  const status = park.current_status || "UNKNOWN";
  const coverImage = destinationCoverImage(park);
  const trailLabel = `${park.trail_count} ${translateLabel(park.trail_count === 1 ? "trail" : "trails")}`;
  const recommendationReason = park.recommendation_reason?.trim();
  const showRecommendationReason =
    recommendationReason && !isTrailCountOnlyRecommendation(recommendationReason, park.trail_count);
  const evidence = buildDiscoveryResultEvidence(park, discoveryCriteria, discoveryPreferences);
  const hasEvidence = evidence.matches.length > 0 || evidence.preferenceMatches.length > 0 || evidence.warnings.length > 0;

  const handleFav = async (e) => {
    e.stopPropagation();
    const res = await toggleFavorite(park.id, "park");
    if (res.needAuth) {
      toast(translateLabel("Sign in to save rides"), { description: translateLabel("Create a free account to build your Want to Ride list.") });
      navigate("/login");
    } else if (res.added) toast.success(translateLabel("{name} added to Want to Ride", { name: park.name }));
    else if (res.removed) toast(translateLabel("{name} removed", { name: park.name }));
    if (!res.needAuth && onFavoriteChanged) await onFavoriteChanged(res);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3) }}
      onMouseEnter={() => onHover && onHover(park.id)}
      onMouseLeave={() => onHover && onHover(null)}
      onClick={() => onSelect && onSelect(park.id)}
      data-testid="park-card"
      data-park-id={park.id}
      data-discovery-bucket={park.discovery_bucket || "exact"}
      className={`group cursor-pointer overflow-hidden rounded-2xl border bg-[#121214] transition-transform hover:-translate-y-0.5 ${
        desktopCompact ? "md:grid md:min-h-32 md:grid-cols-[9rem_minmax(0,1fr)]" : ""
      } ${
        active ? "border-[#CCFF00] shadow-[0_0_0_1px_#CCFF00]" : "border-white/5"
      }`}
    >
      <div className={`relative h-28 w-full overflow-hidden sm:h-32 ${desktopCompact ? "md:h-full md:min-h-32" : ""}`}>
        <img
          src={coverImage}
          alt={park.image ? park.name : `${park.name} default BPF cover`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/20 to-transparent" />
        <div className={`absolute left-3 top-3 flex max-w-[calc(100%-6.5rem)] flex-wrap gap-1.5 ${desktopCompact ? "md:hidden" : ""}`}>
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${TYPE_STYLES[park.type] || "bg-white text-black"}`}>
            {translateLabel(park.type)}
          </span>
        </div>
        <div className="absolute right-3 top-3 flex items-center gap-2">
          {showFavorite && (
            <button
              onClick={handleFav}
              data-testid="card-fav-btn"
              className={`flex h-8 w-8 items-center justify-center rounded-full backdrop-blur-md transition-colors ${
                fav ? "bg-[#FF4500] text-white" : "bg-black/50 text-white hover:bg-black/70"
              }`}
            >
              <HeartIcon size={16} weight={fav ? "fill" : "regular"} />
            </button>
          )}
        </div>
      </div>

      <div className={`space-y-2.5 p-3 sm:space-y-3 sm:p-4 ${desktopCompact ? "md:relative md:flex md:min-h-32 md:min-w-0 md:flex-col md:space-y-1.5 md:p-3" : ""}`}>
        {bucketLabel && (
          <div
            data-testid="discovery-bucket-label"
            className={`w-fit rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
              desktopCompact ? "md:hidden" : ""
            } ${BUCKET_STYLES[park.discovery_bucket] || "border-white/10 bg-white/5 text-zinc-300"}`}
          >
            {bucketLabel}
          </div>
        )}

        {desktopCompact && (
          <div className="hidden md:block text-[9px] font-extrabold uppercase tracking-[0.14em] text-zinc-500">
            {translateLabel(park.type)}
          </div>
        )}

        <div className="flex items-start justify-between gap-2">
          <h3 className={`font-display text-lg font-bold leading-tight text-white sm:text-xl ${desktopCompact ? "md:line-clamp-2 md:text-xl md:leading-[1.05]" : ""}`}>
            {park.name}
          </h3>
          {park.rating_count > 0 && (
            <span className="flex shrink-0 items-center gap-1 rounded-md bg-white/5 px-2 py-1 text-sm font-bold text-white">
              <StarIcon size={14} weight="fill" className="text-[#CCFF00]" />
              {Number(park.rating).toFixed(1)}
            </span>
          )}
        </div>

        {status !== "UNKNOWN" && (
          <div
            data-testid="card-current-status"
            className={`flex items-center gap-1.5 text-[11px] font-bold ${STATUS_STYLES[status] || STATUS_STYLES.UNKNOWN}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT_STYLES[status] || STATUS_DOT_STYLES.UNKNOWN}`} />
            {currentDestinationStatusLabel(status)}
          </div>
        )}

        {showRecommendationReason && (
          <p data-testid="recommendation-reason" className={`text-xs font-semibold text-[#CCFF00] ${desktopCompact ? "md:hidden" : ""}`}>
            {recommendationReason?.match(/^Fits your (.+) style$/)
              ? translateLabel("Fits your {style} style", { style: translateLabel(recommendationReason.match(/^Fits your (.+) style$/)?.[1] || "") })
              : translateLabel(recommendationReason)}
          </p>
        )}

        {evidence.warnings.length > 0 && (
          <div className="space-y-1 text-[11px] leading-4 text-amber-200" data-testid="result-evidence-warnings">
            {evidence.warnings.map((warning) => <p key={warning}>⚠ {warning}</p>)}
          </div>
        )}

        {hasEvidence && (
          <div className={desktopCompact ? "md:pr-20" : ""}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setEvidenceOpen((value) => !value);
              }}
              className={`flex items-center gap-1 font-bold text-zinc-300 transition-colors hover:text-white ${desktopCompact ? "text-[11px] md:text-[10px]" : "text-[11px]"}`}
              aria-expanded={evidenceOpen}
              data-testid="why-this-result-toggle"
            >
              <span className={desktopCompact ? "md:hidden" : ""}>{translateLabel("Why this result?")}</span>
              {desktopCompact && <span className="hidden md:inline">{translateLabel("Why?")}</span>}
              <CaretDownIcon
                size={12}
                weight="bold"
                className={`transition-transform ${evidenceOpen ? "rotate-180" : ""}`}
              />
            </button>

            {evidenceOpen && !desktopCompact && (
              <div
                className="mt-1.5 space-y-1.5 border-l-2 border-[#CCFF00]/35 pl-2.5 text-[11px] leading-4"
                data-testid="why-this-result"
              >
                {evidence.matches.length > 0 && (
                  <div>
                    <div className="font-extrabold uppercase tracking-[0.12em] text-[#CCFF00]">{translateLabel("Matches")}</div>
                    <ul className="mt-1 space-y-1 text-zinc-300">
                      {evidence.matches.map((reason) => <li key={reason}>• {reason}</li>)}
                    </ul>
                  </div>
                )}
                {evidence.preferenceMatches.length > 0 && (
                  <div>
                    <div className="font-extrabold uppercase tracking-[0.12em] text-zinc-500">{translateLabel("Preference fit")}</div>
                    <ul className="mt-1 space-y-1 text-zinc-400">
                      {evidence.preferenceMatches.map((reason) => <li key={reason}>• {reason}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className={`flex items-center gap-3 text-xs font-semibold text-zinc-400 ${desktopCompact ? "md:mt-auto md:min-w-0 md:flex-wrap md:gap-x-3 md:gap-y-1 md:pr-20" : ""}`}>
          {park.drive_minutes != null && (
            <span className="flex shrink-0 items-center gap-1 font-bold text-[#CCFF00]">
              <ClockIcon size={13} /> {formatDriveMinutes(park.drive_minutes)}
            </span>
          )}
          <span className={`flex items-center gap-1 ${desktopCompact ? "md:min-w-0" : ""}`}>
            <MapPinIcon size={13} className="shrink-0" />
            <span className={desktopCompact ? "md:truncate" : ""}>
              {park.distance_km != null ? formatDistanceKm(park.distance_km, unitSystem) : park.city || translateLabel("Location unavailable")}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <PathIcon size={13} /> {trailLabel}
          </span>
        </div>

        <div className={`space-y-1.5 ${desktopCompact ? "md:hidden" : ""}`}>
          <DifficultyBar distribution={park.difficulty_distribution} />
          <DifficultyLegend distribution={park.difficulty_distribution} />
        </div>

        <div className={`flex flex-wrap items-center gap-1.5 pt-1 ${desktopCompact ? "md:hidden" : ""}`}>
          {(park.riding_styles ?? []).slice(0, 4).map((s) => (
            <span key={s} className="rounded-md bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-zinc-300">
              {translateLabel(STYLE_LABELS[s] || s)}
            </span>
          ))}
          {park.access?.length > 0 && (
            <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-[#CCFF00]">
              <CableCarIcon size={13} />
              {park.access.map((a) => translateLabel(ACCESS_LABELS[a] || a)).join(" · ")}
            </span>
          )}
        </div>

        <div className={`mt-1 flex items-center gap-2 ${desktopCompact ? "md:mt-auto md:min-h-5 md:pr-20" : ""}`}>
          {onToggleCompare && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleCompare(park);
              }}
              data-testid="card-compare-btn"
              aria-pressed={compareSelected}
              aria-label={translateLabel(compareSelected ? "Remove {name} from comparison" : "Add {name} to comparison", { name: park.name })}
              title={translateLabel(compareSelected ? "Remove from compare" : "Add to compare")}
              className={`inline-flex items-center gap-1.5 text-[11px] font-extrabold transition-colors ${
                desktopCompact ? "md:p-0 md:border-0 md:bg-transparent" : "rounded-lg border px-2.5 py-2"
              } ${
                compareSelected
                  ? desktopCompact
                    ? "text-[#CCFF00]"
                    : "border-[#CCFF00]/50 bg-[#CCFF00]/12 text-[#CCFF00]"
                  : desktopCompact
                    ? "text-zinc-400 hover:text-[#CCFF00]"
                    : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-[#CCFF00]/30 hover:text-[#CCFF00]"
              }`}
            >
              {compareSelected
                ? <CheckCircleIcon size={13} weight="fill" />
                : <PlusIcon size={13} weight="bold" />}
              <span>{translateLabel(compareSelected ? "Compared" : "Compare")}</span>
            </button>
          )}
          <Link
            to={parkPath(park)}
            onClick={(event) => event.stopPropagation()}
            data-testid="view-park-btn"
            className={`ml-auto text-sm font-bold text-[#CCFF00] transition-colors hover:text-[#B8E600] ${desktopCompact ? "md:absolute md:bottom-3 md:right-3 md:text-xs" : ""}`}
          >
            {translateLabel("View Park")}
          </Link>
        </div>
      </div>

      {desktopCompact && evidenceOpen && (
        <div
          className="hidden border-t border-white/[0.06] px-4 py-3 text-[11px] leading-4 md:col-span-2 md:block"
          data-testid="why-this-result"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="grid gap-3 lg:grid-cols-2">
            {evidence.matches.length > 0 && (
              <div>
                <div className="font-extrabold uppercase tracking-[0.12em] text-[#CCFF00]">{translateLabel("Matches")}</div>
                <ul className="mt-1 space-y-1 text-zinc-300">
                  {evidence.matches.map((reason) => <li key={reason}>• {reason}</li>)}
                </ul>
              </div>
            )}
            {evidence.preferenceMatches.length > 0 && (
              <div>
                <div className="font-extrabold uppercase tracking-[0.12em] text-zinc-500">{translateLabel("Preference fit")}</div>
                <ul className="mt-1 space-y-1 text-zinc-400">
                  {evidence.preferenceMatches.map((reason) => <li key={reason}>• {reason}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
